import { useState, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type TableOptions,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { useTranslation } from "react-i18next";

interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  totalCount?: number;
  serverSide?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  rowsPerPageOptions?: number[];
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  getRowClassName?: (row: TData) => string | undefined;
}

function DataTable<TData>({
  columns,
  data,
  totalCount,
  serverSide = false,
  loading = false,
  emptyMessage,
  rowsPerPageOptions = [10, 25, 50],
  sorting: controlledSorting,
  onSortingChange,
  pagination: controlledPagination,
  onPaginationChange,
  getRowClassName,
}: DataTableProps<TData>) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const resolvedEmptyMessage = emptyMessage ?? t("usage.noRecords");
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize: rowsPerPageOptions[0] || 10,
    },
  );

  const sorting = controlledSorting ?? internalSorting;
  const pagination = controlledPagination ?? internalPagination;

  const handleSortingChange: TableOptions<TData>["onSortingChange"] = (
    updater,
  ) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    if (onSortingChange) onSortingChange(next);
    else setInternalSorting(next);
  };

  const handlePaginationChange: TableOptions<TData>["onPaginationChange"] = (
    updater,
  ) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    if (onPaginationChange) onPaginationChange(next);
    else setInternalPagination(next);
  };

  const table = useReactTable({
    data,
    columns,
    state: { sorting, pagination },
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    ...(serverSide
      ? {
          manualSorting: true,
          manualPagination: true,
          pageCount: totalCount
            ? Math.ceil(totalCount / pagination.pageSize)
            : -1,
        }
      : {
          getSortedRowModel: getSortedRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
        }),
  });

  const totalPages = table.getPageCount();
  const currentPage = pagination.pageIndex;

  const getPageNumbers = useCallback((): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0);
      if (currentPage > 2) pages.push("...");
      for (
        let i = Math.max(1, currentPage - 1);
        i <= Math.min(totalPages - 2, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 3) pages.push("...");
      pages.push(totalPages - 1);
    }
    return pages;
  }, [totalPages, currentPage]);

  return (
    <div className="overflow-hidden rounded-[8px] border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="border-b border-border bg-muted/30"
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      "px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-muted-foreground",
                      header.column.getCanSort() &&
                        "cursor-pointer select-none",
                    )}
                    style={{ minWidth: header.column.columnDef.minSize }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center gap-1">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {header.column.getCanSort() && (
                        <span className="text-muted-foreground/60">
                          {{
                            asc: <ChevronUp className="size-3.5" />,
                            desc: <ChevronDown className="size-3.5" />,
                          }[header.column.getIsSorted() as string] ?? (
                            <ChevronsUpDown className="size-3.5" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: pagination.pageSize }).map((_, i) => (
                <tr
                  key={`skeleton-${i}`}
                  className="border-b border-border last:border-b-0"
                >
                  {columns.map((_, colIdx) => (
                    <td key={colIdx} className="px-4 py-3">
                      <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
                    </td>
                  ))}
                </tr>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-sm text-muted-foreground"
                >
                  {resolvedEmptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    "border-b border-border bg-background transition-colors last:border-b-0 hover:bg-muted/20",
                    getRowClassName?.(row.original),
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-3 text-sm text-foreground"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-border bg-muted/30 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t("common.rowsPerPage")}
          </span>
          <select
            value={pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="h-8 min-w-[64px] rounded-md border border-border bg-card px-2 text-xs text-foreground outline-hidden focus:border-primary"
          >
            {rowsPerPageOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1">
          <button
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
            className="flex items-center gap-0.5 rounded-md px-1 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground"
          >
            {isRTL ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronLeft className="size-4" />
            )}
            <span>{t("common.previous")}</span>
          </button>

          {getPageNumbers().map((p, i) =>
            p === "..." ? (
              <span
                key={`dots-${i}`}
                className="px-1 text-xs text-muted-foreground"
              >
                •••
              </span>
            ) : (
              <button
                key={p}
                onClick={() => table.setPageIndex(p as number)}
                className={cn(
                  "flex size-8 items-center justify-center rounded-lg text-xs transition-colors",
                  currentPage === p
                    ? "border border-border bg-muted font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-muted/50",
                )}
              >
                {(p as number) + 1}
              </button>
            ),
          )}

          <button
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
            className="flex items-center gap-0.5 rounded-md px-1 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground"
          >
            <span>{t("common.next")}</span>
            {isRTL ? (
              <ChevronLeft className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
export type { DataTableProps };
